<template>
    <main
        class="min-w-0 flex-1 border-t border-taupe-200 lg:flex overflow-y-auto overflow-x-hidden"
    >
        <!-- Primary column -->
        <section
            aria-labelledby="primary-heading"
            class="flex h-full min-w-0 flex-1 flex-col lg:order-last pt-2 sm:pt-4 lg:pt-6 lg:max-w-7xl lg:mx-auto"
        >
            <h1 id="primary-heading" class="sr-only">Project Contexts</h1>
            <div class="px-4 sm:px-6 lg:px-8">
                <nav class="flex items-center gap-2 text-sm">
                    <router-link
                        :to="{
                            name: 'ProjectContextsScreen',
                            params: environmentRouteParams,
                        }"
                        class="text-taupe-500 hover:text-sky-500"
                    >
                        Contexts
                    </router-link>
                </nav>
                <div
                    v-if="!loading && result"
                    class="mt-1 flex items-center gap-3"
                >
                    <h1 class="text-2xl font-medium text-taupe-800">
                        {{ result.name }}
                    </h1>
                    <zen-inline-pill :value="result.type" />
                </div>

                <zen-loading-indicator v-if="loading" />

                <div v-if="!loading && result" class="lg:flex lg:space-x-8">
                    <div class="mt-8 w-full lg:w-1/3">
                        <div>
                            <div
                                class="mb-6 rounded-xl border border-taupe-200 bg-white shadow-xs"
                            >
                                <dl class="divide-y divide-taupe-100">
                                    <div
                                        class="rounded-t-xl bg-taupe-50 px-4 py-4 sm:px-5"
                                    >
                                        <h3
                                            class="text-base font-medium text-taupe-800"
                                        >
                                            Detail
                                        </h3>
                                    </div>
                                    <div
                                        class="grid grid-cols-[110px_minmax(0,1fr)] items-center gap-x-4 px-4 py-4 sm:px-5"
                                    >
                                        <dt
                                            class="text-xs font-medium uppercase tracking-wide text-taupe-500"
                                        >
                                            Identifier
                                        </dt>
                                        <dd
                                            class="min-w-0 text-sm font-mono leading-6 text-taupe-900"
                                        >
                                            <zen-tooltip
                                                :content="
                                                    identifierTooltipContent
                                                "
                                                placement="right"
                                                v-slot="{
                                                    setReference,
                                                    ariaAttrs,
                                                    show,
                                                    hide,
                                                }"
                                            >
                                                <button
                                                    type="button"
                                                    :ref="setReference"
                                                    v-bind="ariaAttrs"
                                                    class="block w-full truncate text-left cursor-pointer hover:text-sky-500 transition-colors"
                                                    @mouseenter="show"
                                                    @mouseleave="hide"
                                                    @focus="show"
                                                    @blur="hide"
                                                    v-clipboard:copy="
                                                        result.identifier
                                                    "
                                                    v-clipboard:success="
                                                        onCopyIdentifier
                                                    "
                                                    v-clipboard:error="
                                                        onCopyIdentifierError
                                                    "
                                                >
                                                    {{ result.identifier }}
                                                </button>
                                            </zen-tooltip>
                                        </dd>
                                    </div>
                                    <div
                                        class="grid grid-cols-[110px_minmax(0,1fr)] items-center gap-x-4 px-4 py-4 sm:px-5"
                                    >
                                        <dt
                                            class="text-xs font-medium uppercase tracking-wide text-taupe-500"
                                        >
                                            Last seen
                                        </dt>
                                        <dd
                                            class="text-sm leading-6 text-taupe-900"
                                        >
                                            <zen-date-time
                                                human
                                                :value="result.last_seen_at"
                                                blank="Never"
                                            />
                                        </dd>
                                    </div>
                                </dl>
                            </div>

                            <div
                                class="mb-6 mt-3 rounded-xl border border-taupe-200 bg-white shadow-xs"
                            >
                                <div
                                    class="flex items-center justify-between rounded-t-xl bg-taupe-50 border-b border-taupe-100 px-4 py-4 sm:px-5"
                                >
                                    <h3
                                        class="text-base font-medium text-taupe-800"
                                    >
                                        Additional Attributes
                                    </h3>
                                    <div
                                        v-if="hasAttributes"
                                        class="flex space-x-3 text-sm uppercase text-taupe-500"
                                    >
                                        <button
                                            @click="toggleAttributeView"
                                            :disabled="showAttributeTable"
                                            :class="
                                                showAttributeTable
                                                    ? 'text-taupe-800'
                                                    : 'cursor-pointer text-taupe-500 hover:text-sky-500'
                                            "
                                        >
                                            Table
                                        </button>
                                        <span>|</span>
                                        <button
                                            @click="toggleAttributeView"
                                            :disabled="showAttributeJson"
                                            :class="
                                                showAttributeJson
                                                    ? 'text-taupe-800'
                                                    : 'cursor-pointer text-taupe-500 hover:text-sky-500'
                                            "
                                        >
                                            JSON
                                        </button>
                                    </div>
                                </div>
                                <div v-if="!hasAttributes" class="p-4">
                                    <zen-empty-table-message
                                        compact
                                        icon="ZenIconInfo"
                                    >
                                        This context does not have additional
                                        attributes.
                                    </zen-empty-table-message>
                                </div>
                                <dl
                                    v-if="showAttributeTable"
                                    class="divide-y divide-taupe-100"
                                >
                                    <div
                                        v-for="attribute in result.attributes
                                            .data"
                                        :key="attribute.ulid"
                                        class="grid grid-cols-[110px_minmax(0,1fr)] items-start gap-x-4 px-4 py-4 sm:px-5"
                                    >
                                        <dt
                                            class="text-xs font-medium uppercase tracking-wide text-taupe-500"
                                        >
                                            {{ attribute.key }}
                                        </dt>
                                        <dd
                                            v-if="attribute.values != null"
                                            class="font-mono text-sm leading-6 text-taupe-900"
                                        >
                                            <div
                                                v-for="value in attribute.values
                                                    .data"
                                                :key="value.ulid"
                                                class="break-words"
                                            >
                                                {{ value.value }}
                                            </div>
                                        </dd>
                                        <dd
                                            v-if="attribute.values == null"
                                            class="font-mono text-sm italic leading-6 text-taupe-500"
                                        >
                                            null
                                        </dd>
                                    </div>
                                </dl>
                                <div
                                    v-if="showAttributeJson"
                                    class="rounded-b-xl bg-taupe-50 p-4"
                                >
                                    <pre
                                        class="max-h-80 overflow-auto whitespace-pre-wrap break-words font-mono text-xs leading-5 text-taupe-800"
                                    ><!-- eslint-disable-next-line vue/no-v-html --><code
                                        v-html="highlightedAttributeJson"
                                    ></code></pre>
                                </div>
                            </div>

                            <div class="mt-3 mb-6">
                                <zen-grid
                                    :empty="
                                        (result?.segments?.data?.length ??
                                            0) === 0
                                    "
                                >
                                    <template v-slot:title>Segments</template>
                                    <template v-slot:actions>
                                        <zen-button
                                            v-if="
                                                can(
                                                    PERMISSIONS.SEGMENT_UPDATE,
                                                    permissionScope
                                                )
                                            "
                                            variant="primary"
                                            size="small"
                                            @click="openCreateSegment()"
                                        >
                                            Add a segment
                                        </zen-button>
                                    </template>
                                    <zen-grid-table>
                                        <zen-grid-header>
                                            <zen-grid-header-row>
                                                <zen-grid-header-cell
                                                    :hidable="false"
                                                    title="Name"
                                                />
                                                <zen-grid-header-cell
                                                    alignment="center"
                                                    title="Actions"
                                                />
                                            </zen-grid-header-row>
                                        </zen-grid-header>
                                        <zen-grid-body
                                            :results="filteredResultSegments"
                                            :loading="false"
                                        >
                                            <zen-grid-body-row
                                                v-for="segment in filteredResultSegments"
                                                :key="segment.ulid"
                                            >
                                                <zen-grid-body-cell
                                                    :hidable="false"
                                                    :primary="true"
                                                >
                                                    <zen-grid-body-cell-primary-link
                                                        :to="{
                                                            name: 'ProjectSegmentDetailScreen',
                                                            params: {
                                                                projectKey:
                                                                    currentEnvironment.projectKey,
                                                                environmentKey:
                                                                    currentEnvironment.environmentKey,
                                                                segmentUlid:
                                                                    segment.ulid,
                                                            },
                                                        }"
                                                    >
                                                        {{ segment.name }}
                                                    </zen-grid-body-cell-primary-link>

                                                    <zen-grid-mobile-detail>
                                                        <zen-grid-mobile-detail-item
                                                            label="Actions"
                                                        >
                                                            <zen-button
                                                                v-if="
                                                                    can(
                                                                        PERMISSIONS.SEGMENT_UPDATE,
                                                                        permissionScope
                                                                    )
                                                                "
                                                                variant="link-danger"
                                                                @click="
                                                                    openRemoveSegmentModal(
                                                                        segment.ulid
                                                                    )
                                                                "
                                                            >
                                                                Remove
                                                            </zen-button>
                                                        </zen-grid-mobile-detail-item>
                                                    </zen-grid-mobile-detail>
                                                </zen-grid-body-cell>
                                                <zen-grid-body-cell
                                                    alignment="center"
                                                >
                                                    <zen-table-icon
                                                        v-if="
                                                            can(
                                                                PERMISSIONS.SEGMENT_UPDATE,
                                                                permissionScope
                                                            )
                                                        "
                                                        @click="
                                                            openRemoveSegmentModal(
                                                                segment.ulid
                                                            )
                                                        "
                                                        :srText="
                                                            'Remove ' +
                                                            segment.name +
                                                            ' Segment'
                                                        "
                                                        tooltipText="Remove"
                                                    >
                                                        <zen-icon-remove />
                                                    </zen-table-icon>
                                                </zen-grid-body-cell>
                                            </zen-grid-body-row>
                                        </zen-grid-body>
                                    </zen-grid-table>
                                    <template v-slot:filters>
                                        <input
                                            type="search"
                                            id="segment-search"
                                            name="segment-search"
                                            v-model="filters.segmentSearch"
                                            autocomplete="off"
                                            class="block w-full rounded-md border-taupe-300 shadow-xs focus:border-taupe-300 focus:ring-taupe-500 sm:text-sm lg:text-base"
                                            placeholder="Filter by segment name..."
                                            aria-label="Filter by segment name"
                                        />
                                    </template>
                                    <template v-slot:empty>
                                        <zen-empty-table-message
                                            compact
                                            icon="ZenIconFilter"
                                        >
                                            This context is not associated with
                                            any segments.
                                        </zen-empty-table-message>
                                    </template>
                                </zen-grid>
                            </div>
                        </div>
                    </div>

                    <div class="my-8 w-full lg:w-2/3">
                        <div>
                            <div v-if="!evaluations.loading">
                                <zen-grid :flush="true">
                                    <template v-slot:title
                                        >Flag Evaluation</template
                                    >
                                    <zen-grid-table>
                                        <zen-grid-header>
                                            <zen-grid-header-row>
                                                <zen-grid-header-cell
                                                    :hidable="false"
                                                    title="Flag"
                                                />
                                                <zen-grid-header-cell
                                                    alignment="center"
                                                    title="Type"
                                                />
                                                <zen-grid-header-cell
                                                    title="Value"
                                                />
                                            </zen-grid-header-row>
                                        </zen-grid-header>
                                        <zen-grid-body
                                            :results="filteredEvaluationResults"
                                            :loading="evaluations.loading"
                                        >
                                            <zen-grid-body-row
                                                v-for="evaluation in filteredEvaluationResults"
                                                :key="evaluation.flag.key"
                                            >
                                                <zen-grid-body-cell
                                                    :hidable="false"
                                                    :primary="true"
                                                >
                                                    <div
                                                        class="font-medium text-taupe-800"
                                                    >
                                                        {{
                                                            evaluation.flag.key
                                                        }}
                                                    </div>
                                                    <div
                                                        v-if="
                                                            evaluation.flag.name
                                                        "
                                                        class="text-xs text-taupe-500"
                                                    >
                                                        {{
                                                            evaluation.flag.name
                                                        }}
                                                    </div>
                                                    <zen-grid-mobile-detail>
                                                        <zen-grid-mobile-detail-item
                                                            label="Type"
                                                        >
                                                            <zen-inline-pill
                                                                :value="
                                                                    evaluation
                                                                        .flag
                                                                        .type
                                                                "
                                                            />
                                                        </zen-grid-mobile-detail-item>
                                                        <zen-grid-mobile-detail-item
                                                            label="Value"
                                                        >
                                                            <span
                                                                class="font-mono text-xs"
                                                                >{{
                                                                    evaluation
                                                                        .flag
                                                                        .value
                                                                }}</span
                                                            >
                                                        </zen-grid-mobile-detail-item>
                                                    </zen-grid-mobile-detail>
                                                </zen-grid-body-cell>
                                                <zen-grid-body-cell
                                                    alignment="center"
                                                >
                                                    <zen-inline-pill
                                                        :value="
                                                            evaluation.flag.type
                                                        "
                                                    />
                                                </zen-grid-body-cell>
                                                <zen-grid-body-cell>
                                                    <span
                                                        class="font-mono text-xs"
                                                    >
                                                        {{
                                                            evaluation.flag
                                                                .value
                                                        }}
                                                    </span>
                                                </zen-grid-body-cell>
                                            </zen-grid-body-row>
                                        </zen-grid-body>
                                    </zen-grid-table>
                                    <template v-slot:filters>
                                        <input
                                            type="search"
                                            id="evaluation-search"
                                            name="evaluation-search"
                                            v-model="filters.evaluationSearch"
                                            autocomplete="off"
                                            class="block w-full rounded-md border-taupe-300 shadow-xs focus:border-taupe-300 focus:ring-taupe-500 sm:text-sm lg:text-base"
                                            placeholder="Filter by flag name or key..."
                                            aria-label="Filter by flag name or key"
                                        />
                                    </template>
                                </zen-grid>
                            </div>
                            <zen-empty-table-message
                                v-if="
                                    !evaluations.loading &&
                                    filteredEvaluationResults.length == 0
                                "
                                title="No flags to evaluate"
                                icon="ZenIconFlag"
                            >
                                There are no flags to evaluate for this context.
                            </zen-empty-table-message>
                        </div>
                    </div>
                </div>
            </div>
        </section>

        <create-context-segment-slide-in
            :is-open="modals.createSegment.show"
            v-model:search="modals.createSegment.data.search"
            :error="modals.createSegment.error"
            :creating="modals.createSegment.creating"
            :creating-key="modals.createSegment.creatingKey"
            :results="segmentSearchResults"
            @close="toggleCreateSegmentModal"
            @add="confirmCreateSegment"
        />

        <remove-context-segment-modal
            :is-open="modals.removeSegment.show"
            @close="toggleRemoveSegmentModal"
            @confirm="confirmRemoveSegment"
        />
    </main>
</template>

<script>
import ContextService from '@/services/context.service'
import ContextEvaluationService from '@/services/context-evaluation.service'
import SegmentService from '@/services/segment.service'
import CreateContextSegmentSlideIn from '@/screens/trusted/project/modals/CreateContextSegmentSlideIn.vue'
import RemoveContextSegmentModal from '@/screens/trusted/project/modals/RemoveContextSegmentModal.vue'

import { errorMessage, infoMessage } from '@/utils/notifications'
import { removeBy, upsertBy } from '@/utils/list-mutations'
import { usePermissions, PERMISSIONS } from '@/composables/usePermissions'
import { useCurrentEnvironment } from '@/composables/useCurrentEnvironment'

export default {
    name: 'ProjectContextDetailScreen',
    components: {
        CreateContextSegmentSlideIn,
        RemoveContextSegmentModal,
    },
    setup() {
        const { can } = usePermissions()
        const { current: currentEnvironment } = useCurrentEnvironment()
        return { can, PERMISSIONS, currentEnvironment }
    },
    computed: {
        environmentRouteParams() {
            return {
                projectKey: this.currentEnvironment.projectKey,
                environmentKey: this.currentEnvironment.environmentKey,
            }
        },
        hasEnvironmentRouteParams() {
            return (
                this.environmentRouteParams.projectKey !== null &&
                this.environmentRouteParams.environmentKey !== null
            )
        },
        permissionScope() {
            return {
                projectUlid: this.currentEnvironment.projectUlid,
                environmentUlid: this.currentEnvironment.environmentUlid,
            }
        },
        currentSegmentUlids() {
            if (this.result == null) {
                return []
            }

            return this.result.segments.data.map((s) => s.ulid)
        },
        identifierTooltipContent() {
            return `${this.result.identifier} — Click to copy`
        },
        hasAttributes() {
            return (
                this.result.attributes != null &&
                this.result.attributes.data.length > 0
            )
        },
        segmentSearchResults() {
            let matches = 0

            return this.modals.createSegment.data.results.filter((segment) => {
                if (
                    (segment.name
                        .toLowerCase()
                        .includes(
                            this.modals.createSegment.data.search.toLowerCase()
                        ) ||
                        this.modals.createSegment.data.search === '') &&
                    !this.currentSegmentUlids.includes(segment.ulid) &&
                    matches < 10
                ) {
                    matches++
                    return segment
                }
            })
        },
        showAttributeJson() {
            return this.hasAttributes && this.attributeView == 'json'
        },
        showAttributeTable() {
            return this.hasAttributes && this.attributeView == 'table'
        },
        formattedAttributeJson() {
            try {
                const raw = this.result?.attribute_json ?? {}
                const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw
                return JSON.stringify(parsed, null, 2)
            } catch {
                const raw = this.result?.attribute_json
                return typeof raw === 'string' ? raw : '{}'
            }
        },
        highlightedAttributeJson() {
            return this.syntaxHighlightJson(this.formattedAttributeJson)
        },
        filteredResultSegments() {
            const search = (this.filters.segmentSearch || '')
                .trim()
                .toLowerCase()
            const segments = this.result?.segments?.data ?? []
            if (search === '') {
                return segments
            }
            return segments.filter((segment) =>
                (segment.name || '').toLowerCase().includes(search)
            )
        },
        filteredEvaluationResults() {
            const search = this.filters.evaluationSearch.trim().toLowerCase()
            if (search === '') {
                return this.evaluations.results
            }

            return this.evaluations.results.filter((evaluation) => {
                const key = (evaluation.flag.key || '').toLowerCase()
                const name = (evaluation.flag.name || '').toLowerCase()

                return key.includes(search) || name.includes(search)
            })
        },
    },
    data() {
        return {
            loading: true,
            result: null,
            attributeView: 'table',
            filters: {
                evaluationSearch: '',
                segmentSearch: '',
            },
            evaluations: {
                loading: false,
                results: [],
            },
            modals: {
                createSegment: {
                    creating: false,
                    creatingKey: null,
                    error: null,
                    loading: false,
                    show: false,
                    data: {
                        search: '',
                        results: [],
                    },
                },
                removeSegment: {
                    show: false,
                    ulid: null,
                },
            },
        }
    },
    methods: {
        onCopyIdentifier(_e) {
            infoMessage('Identifier has been copied to your clipboard.')
        },
        onCopyIdentifierError(_e) {
            errorMessage('Failed to copy identifier to your clipboard.')
        },
        escapeHtml(value) {
            return value
                .replaceAll('&', '&amp;')
                .replaceAll('<', '&lt;')
                .replaceAll('>', '&gt;')
        },
        syntaxHighlightJson(value) {
            const escaped = this.escapeHtml(value)

            return escaped.replace(
                /("(\\u[a-zA-Z0-9]{4}|\\[^u]|[^\\"])*"(?::)?|\b(true|false|null)\b|-?\d+(?:\.\d*)?(?:[eE][+-]?\d+)?)/g,
                (token) => {
                    let className = 'cd-json-number'

                    if (token.startsWith('"')) {
                        className = token.endsWith(':')
                            ? 'cd-json-key'
                            : 'cd-json-string'
                    } else if (token === 'true' || token === 'false') {
                        className = 'cd-json-boolean'
                    } else if (token === 'null') {
                        className = 'cd-json-null'
                    }

                    return `<span class="${className}">${token}</span>`
                }
            )
        },
        confirmCreateSegment(segment) {
            if (this.modals.createSegment.creating) {
                return
            }

            this.modals.createSegment.creating = true
            this.modals.createSegment.creatingKey = segment.ulid
            this.modals.createSegment.error = null

            ContextService.attachSegment(
                this.currentEnvironment.projectKey,
                this.currentEnvironment.environmentKey,
                this.$route.params.contextUlid,
                segment.ulid
            )
                .then(
                    (response) => {
                        this.result.segments.data = upsertBy(
                            this.result.segments.data,
                            response.data
                        )
                    },
                    (error) => {
                        this.modals.createSegment.error = error
                    }
                )
                .finally(() => {
                    this.modals.createSegment.creating = false
                    this.modals.createSegment.creatingKey = null
                })
        },
        confirmRemoveSegment() {
            ContextService.detachSegment(
                this.currentEnvironment.projectKey,
                this.currentEnvironment.environmentKey,
                this.$route.params.contextUlid,
                this.modals.removeSegment.ulid
            ).then(
                () => {
                    this.result.segments.data = removeBy(
                        this.result.segments.data,
                        this.modals.removeSegment.ulid
                    )
                    this.toggleRemoveSegmentModal()
                },
                (error) => {
                    errorMessage(error)
                }
            )
        },
        openCreateSegment() {
            if (!this.hasEnvironmentRouteParams) {
                return
            }

            this.toggleCreateSegmentModal()
            this.modals.createSegment.loading = true
            SegmentService.list(
                this.currentEnvironment.projectKey,
                this.currentEnvironment.environmentKey
            ).then(
                (data) => {
                    this.modals.createSegment.data.results = data.data
                    this.modals.createSegment.loading = false
                    this.modals.createSegment.error = null
                },
                (error) => {
                    this.modals.createSegment.error = error
                    this.modals.createSegment.loading = false
                }
            )
        },
        openRemoveSegmentModal(ulid) {
            this.modals.removeSegment.ulid = ulid
            this.toggleRemoveSegmentModal()
        },
        refresh() {
            if (!this.hasEnvironmentRouteParams) {
                this.loading = false
                return
            }

            this.loading = true
            ContextService.get(
                this.currentEnvironment.projectKey,
                this.currentEnvironment.environmentKey,
                this.$route.params.contextUlid
            ).then(
                (data) => {
                    this.result = data.data
                    this.loading = false
                },
                async () => {
                    await this.$router.push({
                        name: 'ProjectContextsScreen',
                        params: this.environmentRouteParams,
                    })
                }
            )
        },
        refreshEvaluation() {
            if (!this.hasEnvironmentRouteParams) {
                this.evaluations.loading = false
                return
            }

            this.evaluations.loading = true
            ContextEvaluationService.get(
                this.currentEnvironment.projectKey,
                this.currentEnvironment.environmentKey,
                this.$route.params.contextUlid
            )
                .then((data) => {
                    this.evaluations.results = data.data
                })
                .catch((error) => {
                    errorMessage(error)
                })
                .finally(() => {
                    this.evaluations.loading = false
                })
        },
        toggleCreateSegmentModal() {
            this.modals.createSegment.show = !this.modals.createSegment.show
            if (!this.modals.createSegment.show) {
                this.modals.createSegment.data.search = ''
                this.modals.createSegment.error = null
            }
        },
        toggleAttributeView() {
            this.attributeView =
                this.attributeView == 'table' ? 'json' : 'table'
        },
        toggleRemoveSegmentModal() {
            this.modals.removeSegment.show = !this.modals.removeSegment.show
            if (!this.modals.removeSegment.show) {
                this.modals.removeSegment.ulid = null
            }
        },
    },
    mounted() {
        this.refresh()
        this.refreshEvaluation()
    },
}
</script>

<style scoped>
:deep(.cd-json-key) {
    color: #9f1239;
}

:deep(.cd-json-string) {
    color: #166534;
}

:deep(.cd-json-number) {
    color: #1d4ed8;
}

:deep(.cd-json-boolean) {
    color: #7c3aed;
}

:deep(.cd-json-null) {
    color: #6b7280;
    font-style: italic;
}
</style>
